import React, { useEffect, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  Grid,
  Divider,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  createNewVideo,
  updateVideoById,
  viewVideoById,
} from '../../services/content.service';
// import { getSearchListChannels } from '../../services/channel.service';
import Status from '../form-elements/status.component';
import Type from '../form-elements/type.component';
import {
  CONTENT_ALIAS,
  LS_ADMIN_USER_ID,
} from '../../config/const.config';
import { getLocalValue } from '../../utils/local-cache.util';
// import SearchableChannel from '../form-elements/searchableChannel.component';
import SearchableCategory from '../form-elements/searchableCategory.component';
import Series from '../form-elements/series.component';
import Seasons from '../form-elements/seasons.component';
import Show from '../form-elements/show.component';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const AddEditVideos = ({
  title,
  dataId,
  onCancel,
  onSuccess,
  adminUserRole,
  selectedSeason,
  seasonSeries,
  selectedShow,
}) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      title: '',
      // channels: '',
      description: '',
      longDescription: '',
      featuring: '',
      tags: '',
      status: adminUserRole === 'CONTENT_OWNER' ? 0 : null,
      duration: null,
      videoUrl: '',
      contentType: 'MOVIE',
      thumbnail: '',
      heroImage: '',
      categories: [],
      show: '',

    },
  });
  const [loading, setLoading] = useState(true);
  const [contentType, setContentType] = useState('MOVIE');
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [updateSeason, setUpdateSeason] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const saveNewData = (payload) => {
    createNewVideo(payload)
      .then(() => {
        onSuccess(
          `New ${contentType === 'PODCAST' ? CONTENT_ALIAS.AUDIO_SINGULAR :CONTENT_ALIAS.VIDEO_SINGULAR} added successfully.`
        );
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const updateExistingData = (payload) => {
    updateVideoById(dataId, payload)
      .then(() => {
        onSuccess(
          `${contentType === 'PODCAST' ? CONTENT_ALIAS.AUDIO_SINGULAR :CONTENT_ALIAS.VIDEO_SINGULAR} details updated successfully.`
        );
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };


  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
  
    const payload = {
      title: data.title.trim() || '',
      description: data.description.trim() || '',
      // channels: channels || [],
      longDescription: data.longDescription.trim() || '',
      featuring: data.featuring || '',
      tags: data.tags || '',
      status: data.status,
      duration: data.duration || 0,
      videoUrl: data.videoUrl || '',
      contentType: data.contentType,
      videoType: data.contentType === 'PODCAST' ? 'AUDIO' : 'VIDEO',
      thumbnail: data.thumbnail || '',
      heroImage: data.heroImage || '',
    };
    if (data.contentType === 'MOVIE') {
      const categoriesIdStrArr = [];
      if (typeof data.categories === 'object') {
        data.categories.forEach((item) => {
          const categoryId = item.id;

          categoriesIdStrArr.push(`${categoryId}`);
        });
      }
      payload.categories = categoriesIdStrArr || [];
    } else if (data.contentType === 'EPISODE'){
      payload.seriesId = data.series;
      payload.seasonId = data.seasons;
    }
    else{
      payload.showId = data.show;
    }
    if (dataId) {
      updateExistingData(payload);
    } else {
      const adminUserId = getLocalValue(LS_ADMIN_USER_ID);
      payload.adminUserId = adminUserId || null;
      saveNewData(payload);
    }
  };

  useEffect(() => {
    if (!dataId) {
      setLoading(false);
      return;
    }

    viewVideoById(dataId)
      .then((res) => {
       
        // Fill form values
        setValue('title', res.data?.title || '');
        setValue('description', res.data?.description || '');
        // setValue('channels', res.data?.channels || null);
        setValue('longDescription', res.data?.longDescription || '');
        setValue('featuring', res.data?.featuring || '');
        setValue('tags', res.data?.tags || '');
        setValue('status', res.data?.status || 0);
        setValue('duration', res.data?.duration || 0);
        setValue('videoUrl', res.data?.videoUrl || '');
        setValue('thumbnail', res.data?.thumbnailUrl || '');
        setValue('heroImage', res.data?.heroImageUrl || '');
        setValue('contentType', res.data.contentType || 'MOVIE');
        setContentType(res.data.contentType || 'MOVIE');
        if (res.data.contentType === 'EPISODE') {
          setValue('series', res.data.seriesId);
          setValue('seasons', res.data.seasonId);
          setSelectedSeries(res.data.seriesId);
          setUpdateSeason(true);
        } else if (res.data.contentType === 'MOVIE'){
          setValue('categories', res.data?.categories || []);
        } else {
          setValue('show', res.data.showId);
        }
        
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [dataId]);

  useEffect(() => {
    if (selectedSeason){
      setContentType('EPISODE');
      setValue('contentType', 'EPISODE');
      setSelectedSeries(seasonSeries);
      setValue('series', seasonSeries);
      setValue('seasons', selectedSeason);
    }
    if(selectedShow){
      setContentType('PODCAST');
      setValue('contentType', 'PODCAST');
      setValue('show', selectedShow);
    }
  },[selectedSeason, selectedShow, updateSeason]);

  const onChangeSeason = (value) => {
    setUpdateSeason(value);
  }

  const onUpdatedSeries = (value) => {
    if (value){
      setUpdateSeason(true)
    }
  }

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle id="view-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        {loading && <Loading />}

        {!loading && (
          <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="title"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...{ ...maxLengthValidation(255) },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={`${CONTENT_ALIAS.VIDEO_SINGULAR}/${CONTENT_ALIAS.AUDIO_SINGULAR}  Title`}
                      type="text"
                      error={!!errors.title}
                      helperText={errors?.title?.message || null}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="description"
                  control={control}
                  rules={{ ...maxLengthValidation(250) }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description"
                      type="text"
                      error={!!errors.description}
                      helperText={errors?.description?.message || null}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="longDescription"
                  control={control}
                  rules={{ ...maxLengthValidation(1500) }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Long Description"
                      type="text"
                      error={!!errors.longDescription}
                      helperText={errors?.longDescription?.message || null}
                      fullWidth
                      multiline
                      rows={3}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="contentType"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field }) => (
                    <Type
                      id="content_type"
                      name="content_type"
                      label="Content Type"
                      defaultValue={contentType}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        setContentType(e.target.value);
                      }}
                      validOptions={['MOVIE', 'EPISODE', 'PODCAST']}
                      isDisabled={adminUserRole === 'CONTENT_OWNER' || dataId || selectedSeason || selectedShow}
                      sx={{ width: '100%' }}
                      error={errors?.contentType?.message || ''}
                    />
                  )}
                />
              </Grid>
              {contentType === 'MOVIE' && (
                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="categories"
                    control={control}
                    rules={validationRules.REQUIRED}
                    defaultValue={[]}
                    render={({ field: { onChange, value } }) => (
                      <SearchableCategory
                        id="categories"
                        name="categories"
                        label="Categories"
                        defaultValue={value}
                        multiple
                        onChange={onChange}
                        sx={{ width: '100%' }}
                        error={errors?.categories?.message || ''}
                      />
                    )}
                  />
                </Grid>
              )}
              {/* TODO: Show Series or Season */}
              {contentType === 'EPISODE' && (
                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="series"
                    control={control}
                    rules={validationRules.REQUIRED}
                    defaultValue={[]}
                    render={({ field }) => (
                      <Series
                        id="series"
                        name="series"
                        label="Series"
                        defaultValue={selectedSeries}
                        multiple
                        onChange={(e) => {
                          field.onChange(e.target.value);
                          setSelectedSeries(e.target.value);
                          setValue('seasons', null);
                          setUpdateSeason(true)
                        }}
                        sx={{ width: '100%' }}
                        error={errors?.series?.message || ''}
                        onUpdatedSeries={onUpdatedSeries}
                        disabled={selectedSeason}
                      />
                    )}
                  />
                </Grid>
              )}
              {contentType === 'EPISODE' && (selectedSeries || updateSeason) &&(
                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="seasons"
                    control={control}
                    rules={validationRules.REQUIRED}
                    defaultValue={[]}
                    render={({ field: { onChange, value } }) => (
                      <Seasons
                        id="seasons"
                        name="seasons"
                        label="Season"
                        defaultValue={value}
                        multiple
                        onChange={onChange}
                        sx={{ width: '100%' }}
                        error={errors?.seasons?.message || ''}
                        seriesId={selectedSeries}
                        updateSeason={updateSeason}
                        onChangeSeason={onChangeSeason}
                        disabled={selectedSeason}
                      />
                    )}
                  />
                </Grid>
              )}
              {contentType === "PODCAST" && (
                <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="show"
                  control={control}
                  rules={validationRules.REQUIRED}
                  defaultValue={[]}
                  render={({ field: { onChange, value } }) => (
                    <Show
                      id="show"
                      name="show"
                      label="Show"
                      defaultValue={value}
                      multiple
                      onChange={onChange}
                      sx={{ width: '100%' }}
                      error={errors?.show?.message || ''}
                      seriesId={selectedShow}
                      disabled={selectedShow}
                    />
                  )}
                />
              </Grid>
              )}
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="featuring"
                  control={control}
                  rules={{ ...maxLengthValidation(500) }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Featuring"
                      type="text"
                      error={!!errors.featuring}
                      helperText={errors?.featuring?.message || null}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="tags"
                  control={control}
                  rules={{ ...maxLengthValidation(500) }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Tags"
                      type="text"
                      error={!!errors.tags}
                      helperText={errors?.tags?.message || null}
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {adminUserRole === 'ADMIN' && (
                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="status"
                    control={control}
                    rules={validationRules.REQUIRED}
                    render={({ field: { onChange, value } }) => (
                      <Status
                        id="sub_status"
                        name="sub_status"
                        label="Status"
                        defaultValue={value}
                        onChange={onChange}
                        validOptions={[1, 0]}
                        isDisabled={adminUserRole === 'CONTENT_OWNER'}
                        sx={{ width: '100%'}}
                        error={errors?.status?.message || ''}
                      />
                    )}
                  />
                </Grid>
              )}
              { contentType && (
              <>
                <Grid item xs={12} sm={12} md={12}>
                  <Divider
                    textAlign="center"
                    variant="fullWidth"
                    sx={{
                      fontWeight: 'bold',
                    }}
                  >
                    {`${contentType === "PODCAST" ? CONTENT_ALIAS.AUDIO_SINGULAR : CONTENT_ALIAS.VIDEO_SINGULAR} Details (You can upload video and images from upload section)`}
                  </Divider>
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="videoUrl"
                    control={control}
                    rules={{
                      ...validationRules.OPTIONAL_URL,
                      ...{ ...maxLengthValidation(500) },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={`${contentType === "PODCAST" ? CONTENT_ALIAS.AUDIO_SINGULAR : CONTENT_ALIAS.VIDEO_SINGULAR} Url`}
                        type="text"
                        error={!!errors.videoUrl}
                        helperText={errors?.videoUrl?.message || null}
                        fullWidth
                        disabled
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="duration"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Duration (In Seconds)"
                        type="number"
                        error={!!errors.duration}
                        helperText={errors?.duration?.message || null}
                        fullWidth
                        disabled
                      />
                    )}
                  />
                </Grid>


                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="thumbnail"
                    control={control}
                    rules={{
                      ...validationRules.OPTIONAL_URL,
                      ...{ ...maxLengthValidation(250) },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Thumbnail Url"
                        type="text"
                        error={!!errors.thumbnail}
                        helperText={errors?.thumbnail?.message || null}
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="heroImage"
                    control={control}
                    rules={{
                      ...validationRules.OPTIONAL_URL,
                      ...{ ...maxLengthValidation(250) },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Hero Image Url"
                        type="text"
                        error={!!errors.heroImage}
                        helperText={errors?.heroImage?.message || null}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              </>
              )}
            </Grid>
          </form>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'left', marginLeft: 2 }}>
        <Button
          type="submit"
          form="add-edit-form"
          color="primary"
          variant="contained"
          disabled={loading || formSubmitted}
        >
          Save
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={onCancel}
          disabled={formSubmitted}
        >
          Cancel
        </Button>
      </DialogActions>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

AddEditVideos.propTypes = {
  title: PropTypes.string.isRequired,
  dataId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  options: PropTypes.object.isRequired,
  defaultBooksValue: PropTypes.string.isRequired,
  adminUserRole: PropTypes.string.isRequired,
  selectedSeason: PropTypes.number,
  seasonSeries: PropTypes.number,
  selectedShow: PropTypes.number,
};

AddEditVideos.defaultProps = {
  selectedSeason: null,
  seasonSeries: null,
  selectedShow: null,
};
export default AddEditVideos;
